/**
 * AuthManager - TypeScript wrapper for authentication management
 */
import { User } from 'firebase/auth';
import { userAuth } from './firebase-config';

export interface AuthResult {
  success: boolean;
  error?: string;
  user?: User;
}

export class AuthManager {
  private currentUser: User | null = null;
  private onAuthStateChangedCallback: ((user: User | null) => void) | null = null;

  /**
   * Initialize authentication state monitoring
   */
  initializeAuth(callback: (user: User | null) => void): void {
    this.onAuthStateChangedCallback = callback;
    
    // Monitor auth state changes
    userAuth.onAuthStateChanged((user) => {
      this.currentUser = user;
      if (this.onAuthStateChangedCallback) {
        this.onAuthStateChangedCallback(user);
      }
    });
  }

  /**
   * Get authentication token
   */
  async getToken(): Promise<string | null> {
    try {
      return await userAuth.getToken();
    } catch (error) {
      console.error('❌ Failed to get auth token:', error);
      throw error;
    }
  }

  /**
   * Get current user
   */
  getCurrentUser(): User | null {
    // Always get the current user from Firebase
    return userAuth.getCurrentUser() || this.currentUser;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.currentUser !== null;
  }

  /**
   * Sign out
   */
  async signOut(): Promise<AuthResult> {
    try {
      const result = await userAuth.signOut();
      if (result.success) {
        this.currentUser = null;
        return { success: true };
      } else {
        console.error('❌ Sign out error:', result.error);
        return { success: false, error: result.error };
      }
    } catch (error: any) {
      console.error('❌ Sign out error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Require login (redirect to login page)
   */
  requireLogin(): void {
    window.location.href = '/login';
  }

  /**
   * Sign in with email and password
   */
  async signIn(email: string, password: string): Promise<AuthResult> {
    try {
      const result = await userAuth.signIn(email, password);
      if (result.success && result.user) {
        this.currentUser = result.user;
        return { success: true, user: result.user };
      } else {
        return { success: false, error: result.error };
      }
    } catch (error: any) {
      console.error('❌ Sign in error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Sign up with email and password
   */
  async signUp(email: string, password: string, plan: 'free' | 'paid' | 'poc' = 'free', language: 'ja' | 'en' = 'ja'): Promise<AuthResult> {
    try {
      const result = await userAuth.signUp(email, password, plan, language);
      if (result.success && result.user) {
        this.currentUser = result.user;
        return { success: true, user: result.user };
      } else {
        return { success: false, error: result.error };
      }
    } catch (error: any) {
      console.error('❌ Sign up error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Change password
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<AuthResult> {
    try {
      // Get current user from Firebase
      const user = userAuth.getCurrentUser();
      
      if (!user) {
        console.error('No authenticated user found');
        return { success: false, error: 'not-authenticated' };
      }

      // Use the legacy changePassword method from userAuth
      const result = await userAuth.changePassword(currentPassword, newPassword);
      
      if (result.success) {
        return { success: true };
      } else {
        console.error('❌ Password change error:', result.error);
        return { success: false, error: result.error };
      }

    } catch (error: any) {
      console.error('❌ Password change error:', error);
      
      // Return error code instead of localized message
      let errorCode = 'password-change-failed';
      
      if (error.code === 'auth/wrong-password') {
        errorCode = 'wrong-password';
      } else if (error.code === 'auth/weak-password') {
        errorCode = 'weak-password';
      } else if (error.code === 'auth/too-many-requests') {
        errorCode = 'too-many-requests';
      } else if (error.code === 'auth/requires-recent-login') {
        errorCode = 'requires-recent-login';
      } else if (error.code === 'auth/invalid-credential') {
        errorCode = 'wrong-password'; // Firebase v10 uses this code
      }
      
      console.error('Password change error details:', { code: error.code, message: error.message });
      return { success: false, error: errorCode };
    }
  }
}

// Export singleton instance
export const authManager = new AuthManager();

// Export for testing
export default AuthManager;